<template>
  <div v-if="showBanner" class="cookie-banner">
    <div class="cookie-content">
      <h3>Cookie-Einstellungen</h3>
      <p>Diese Website verwendet Cookies, um Ihr Erlebnis zu verbessern. Sie können wählen, welche Art von Cookies Sie akzeptieren möchten.</p>
      
      <div class="cookie-options">
        <div class="cookie-option">
          <label class="switch">
            <input type="checkbox" v-model="essentialCookies" disabled checked>
            <span class="slider round"></span>
          </label>
          <div class="option-text">
            <h4>Notwendige Cookies</h4>
            <p>Diese Cookies sind für das Funktionieren der Website unerlässlich und können nicht deaktiviert werden.</p>
          </div>
        </div>
        
        <div class="cookie-option">
          <label class="switch">
            <input type="checkbox" v-model="analyticsCookies">
            <span class="slider round"></span>
          </label>
          <div class="option-text">
            <h4>Analyse-Cookies</h4>
            <p>Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.</p>
          </div>
        </div>
        
        <div class="cookie-option">
          <label class="switch">
            <input type="checkbox" v-model="marketingCookies">
            <span class="slider round"></span>
          </label>
          <div class="option-text">
            <h4>Marketing-Cookies</h4>
            <p>Diese Cookies werden verwendet, um Werbung relevanter für Sie zu gestalten.</p>
          </div>
        </div>
      </div>
      
      <div class="cookie-actions">
        <button @click="acceptAllCookies" class="btn-accept-all">Alle akzeptieren</button>
        <button @click="acceptSelectedCookies" class="btn-accept-selected">Auswahl akzeptieren</button>
        <button @click="acceptEssentialOnly" class="btn-accept-essential">Nur notwendige akzeptieren</button>
      </div>
    </div>
  </div>
</template>

<script>
import VueCookies from 'vue-cookies';

export default {
  name: 'CookieBanner',
  props: {
    showBanner: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      essentialCookies: true,
      analyticsCookies: false,
      marketingCookies: false,
      cookieSettings: {}
    };
  },
  mounted() {
    // Check if cookie settings exist
    const cookieSettings = VueCookies.get('cookie-settings');
    if (cookieSettings) {
      this.cookieSettings = JSON.parse(cookieSettings);
      this.essentialCookies = this.cookieSettings.essential;
      this.analyticsCookies = this.cookieSettings.analytics;
      this.marketingCookies = this.cookieSettings.marketing;
    }
  },
  methods: {
    saveCookieSettings() {
      // Create cookie settings object
      this.cookieSettings = {
        essential: this.essentialCookies,
        analytics: this.analyticsCookies,
        marketing: this.marketingCookies
      };
      
      // Save settings as cookie
      VueCookies.set('cookie-settings', JSON.stringify(this.cookieSettings), '30d');
      
      // Emit event for parent components
      this.$emit('cookies-updated', this.cookieSettings);
    },
    acceptAllCookies() {
      this.essentialCookies = true;
      this.analyticsCookies = true;
      this.marketingCookies = true;
      this.saveCookieSettings();
    },
    acceptSelectedCookies() {
      // Essential cookies are always true
      this.essentialCookies = true;
      this.saveCookieSettings();
    },
    acceptEssentialOnly() {
      this.essentialCookies = true;
      this.analyticsCookies = false;
      this.marketingCookies = false;
      this.saveCookieSettings();
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/css/global.scss";

.cookie-banner {
  position: fixed;
  bottom: 0;
  width: 100%;
  background-color: rgba($primary-color, 0.95);
  backdrop-filter: blur(10px);
  color: $text-color;
  z-index: 9999;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba($accent-color, 0.2);
  font-size: 0.95rem;
  transform: translateY(0);
  transition: transform 0.5s ease;
  
  &.hide {
    transform: translateY(100%);
  }
  
  .cookie-content {
    max-width: 900px;
    margin: 0 auto;
    padding: 1.5rem;
    
    h3 {
      color: $accent-color;
      margin-bottom: 1rem;
      font-size: 1.4rem;
    }
    
    p {
      margin-bottom: 1.5rem;
      line-height: 1.6;
    }
  }
  
  .cookie-options {
    margin-bottom: 1.5rem;
  }
  
  .cookie-option {
    display: flex;
    align-items: flex-start;
    padding: 1rem 0;
    border-bottom: 1px solid rgba($accent-color, 0.1);
    
    &:last-child {
      border-bottom: none;
    }
    
    .option-text {
      margin-left: 1rem;
      
      h4 {
        margin: 0 0 0.5rem 0;
        font-size: 1.1rem;
      }
      
      p {
        margin: 0;
        font-size: 0.9rem;
        color: $muted-text-color;
      }
    }
  }
  
  .cookie-actions {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 1.5rem;
    
    button {
      padding: 0.7rem 1.2rem;
      border-radius: 6px;
      font-weight: 600;
      font-size: 0.9rem;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
      }
    }
    
    .btn-accept-all {
      background: $accent-gradient;
      color: white;
      border: none;
      box-shadow: 0 4px 10px rgba($accent-color, 0.3);
    }
    
    .btn-accept-selected {
      background: transparent;
      border: 2px solid $accent-color;
      color: $accent-color;
    }
    
    .btn-accept-essential {
      background: transparent;
      border: 1px solid $muted-text-color;
      color: $muted-text-color;
    }
  }
}

/* Toggle Switch Styles */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba($accent-color, 0.2);
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background: $accent-gradient;
}

input:disabled + .slider {
  opacity: 0.7;
  cursor: not-allowed;
}

input:focus + .slider {
  box-shadow: 0 0 1px $accent-color;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}

@media (max-width: 768px) {
  .cookie-banner {
    .cookie-content {
      padding: 1.2rem;
    }
    
    .cookie-actions {
      flex-direction: column;
      
      button {
        width: 100%;
        margin-bottom: 0.5rem;
      }
    }
  }
}
</style>
